import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Alert,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { monitoramentoService } from '../../services/api';
import KpiCard from '../../components/monitoramento/KpiCard';
import { ChatIAButton } from '../../components/ChatIA';
import AnalisePredicaoCard from '../../components/saude/AnalisePredicaoCard';

interface AgingRow {
  especialidade: string;
  aguardando: number;
  media_dias_espera: number;
  mais_antigo: string;
  mais_recente: string;
}

interface EvolucaoRow {
  mes: string;
  novas_entradas: number;
  aguardando: number;
}

interface PrioridadeRow {
  prioridade: string;
  total: number;
  aguardando: number;
}

interface FilaEsperaData {
  kpis: {
    total_na_fila: number;
    aguardando: number;
    agendados: number;
    media_dias_espera_fila: number;
  };
  porPrioridade: PrioridadeRow[];
  aging: AgingRow[];
  evolucao: EvolucaoRow[];
}

const MonitoramentoFilaEspera: React.FC = () => {
  const { municipioAtivo } = useAuth();
  const [data, setData] = useState<FilaEsperaData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!municipioAtivo) {
      setLoading(false);
      return;
    }
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await monitoramentoService.getFilaEspera(municipioAtivo.id);
        setData(result);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Erro ao carregar dados da fila de espera.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [municipioAtivo]);

  if (!municipioAtivo) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <Typography variant="h5" color="text.secondary">Nenhum municipio selecionado</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          Selecione um municipio para visualizar a fila de espera.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
        <Skeleton variant="text" width="30%" height={48} />
        <Grid container spacing={2} sx={{ mb: 3, mt: 1 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
      </Box>
    );
  }

  const kpis = data?.kpis;
  const porPrioridade = data?.porPrioridade || [];
  const aging = data?.aging || [];
  const evolucao = data?.evolucao || [];

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
      {/* Header */}
      <Typography variant="h4" fontWeight={700} sx={{ mb: 3 }}>
        Fila de Espera
      </Typography>

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            titulo="Total na Fila"
            valor={(kpis?.total_na_fila ?? 0).toLocaleString('pt-BR')}
            cor="#1565c0"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            titulo="Aguardando"
            valor={(kpis?.aguardando ?? 0).toLocaleString('pt-BR')}
            cor="#e65100"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            titulo="Agendados"
            valor={(kpis?.agendados ?? 0).toLocaleString('pt-BR')}
            cor="#2e7d32"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            titulo="Media Dias Espera"
            valor={`${(kpis?.media_dias_espera_fila ?? 0).toLocaleString('pt-BR')} dias`}
            cor="#6a1b9a"
          />
        </Grid>
      </Grid>

      {/* Por Prioridade - Stacked BarChart */}
      {porPrioridade.length > 0 && (
        <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 4 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Distribuicao por Prioridade
          </Typography>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={porPrioridade}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="prioridade" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip
                formatter={(value: any) => Number(value).toLocaleString('pt-BR')}
              />
              <Legend />
              <Bar dataKey="total" name="Total" stackId="a" fill="#1565c0" radius={[0, 0, 0, 0]} />
              <Bar dataKey="aguardando" name="Aguardando" stackId="a" fill="#e65100" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Aging Table */}
      {aging.length > 0 && (
        <Paper elevation={1} sx={{ borderRadius: 2, mb: 4, overflow: 'hidden' }}>
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" fontWeight={600}>
              Tempo de Espera por Especialidade
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Especialidade</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Aguardando</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Media Dias Espera</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Mais Antigo</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Mais Recente</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {aging.map((row, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>{row.especialidade}</TableCell>
                    <TableCell align="right">{row.aguardando.toLocaleString('pt-BR')}</TableCell>
                    <TableCell align="right">{row.media_dias_espera.toLocaleString('pt-BR')}</TableCell>
                    <TableCell align="right">{row.mais_antigo}</TableCell>
                    <TableCell align="right">{row.mais_recente}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Evolucao LineChart */}
      {evolucao.length > 0 && (
        <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Evolucao Mensal
          </Typography>
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={evolucao}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip
                formatter={(value: any) => Number(value).toLocaleString('pt-BR')}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="novas_entradas"
                name="Novas Entradas"
                stroke="#1565c0"
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
              <Line
                type="monotone"
                dataKey="aguardando"
                name="Aguardando"
                stroke="#e65100"
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Empty state */}
      {!kpis && porPrioridade.length === 0 && aging.length === 0 && evolucao.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            Nenhum dado disponivel para a fila de espera.
          </Typography>
        </Box>
      )}

      {municipioAtivo && data && (
        <AnalisePredicaoCard
          dadosContexto={JSON.stringify(data)}
          municipioId={municipioAtivo.id}
          painelId="fila-espera"
          contexto="dashboard"
          manualTrigger
        />
      )}

      <ChatIAButton
        contexto="dashboard"
        dadosContexto={JSON.stringify(data)}
        fabSx={{ bottom: 24, right: 24, top: 'auto' }}
      />
    </Box>
  );
};

export default MonitoramentoFilaEspera;
